import React, {ReactNode, useCallback, useReducer} from 'react';
import {ModalComponent} from 'module/Modal/component/ModalComponent';
import {createAction, createReducer} from 'util/redux';
import {Button, DialogActions, DialogTitle, Typography, useTheme} from '@mui/material';
import {ErrorButton} from 'module/Error/component/ErrorButton';
import {log} from 'common/log';
import {parseServerError} from 'module/Error/util/parseServerError';
import {UNKNOWN_ERROR} from 'module/ConfirmDialog/constant/UnknownError';
import {catchError} from 'rxjs/operators';
import {ObservableInput} from 'rxjs'
import {CustomFieldTO} from 'generated/JavaTO'
import {CustomFieldFormComponent} from 'module/CustomField/component/CustomFieldFormComponent'
import {CFModel, CFModelFlat} from 'module/CustomField/type/CFType'
import {CFimmutable} from 'module/CustomField/util/CFimmutable'
import {css} from '@emotion/react'
import {isEqual, omit} from 'lodash'
import {SetCFCb} from 'module/CustomField/type/CFComponentType'

export enum ConfirmVariant {
	/** Ja = resolve, Nein = reject */
	JN,
	/** Ja = resolve(true), Nein = resolve(false), Abbrechen = reject() */
	JN_CANCEL,
	/** (default) OK = resolve */
	OK,
	/** (default) OK = resolve, "Abbrechen" = reject */
	OK_CANCEL,
	/** error: OK = reject */
	ERROR,
	/** "Jetzt aktualisieren" = resolve, "abbrechen" = reject */
	REFRESH,
	/** "Weiter bearbeiten" = resolve, "Schließen" = reject */
	RETURN,
	/** "Synchronisieren" = resolve(true) "Verwerfen" = resolve(false) "Abbrechen" = reject */
	SYNC_DISCARD_ABORT,
	/** "Übernehmen" = resolve(true) "Zurücksetzen" = resolve(false) "Abbrechen" = reject */
	APPLY_RESET_ABORT,
	/** "Übernehmen" = resolve "Abbrechen" = reject */
	APPLY_ABORT,
	/** "Verwerfen" = resolve "Behalten" = reject */
	DISCARD_KEEP,
	/** "Löschen" = resolve "Abbrechen" = reject */
	DELETE_CANCEL,
	/** "Duplizieren" = resolve "Abbrechen" = reject */
	DUPLICATE_CANCEL,
	/** Zurück zum Login OK = resolve "Abbrechen" = reject" */
	RESET_OK,
	/** Zurück zum Login OK = reject  "Abbrechen" = reject"*/
	RESET_ERROR,
	/** Zurück zum Login ERROR = reject  */
	RESET_VALIDATE_ERROR
}

const fallbackDefaultSize = {height: 360, width: 560}

type AlertParams = Partial<Omit<ConfirmDialogState, 'open' | 'resolve' | 'reject'>>

interface ConfirmDialogState {
	open: boolean
	title: ReactNode
	message: ReactNode
	cfDefs: CustomFieldTO[]
	defaultSize: { height?: number, width?: number }
	formModel: CFModel
	variant: ConfirmVariant
	resolve?: (result?: any) => void
	reject?: (reason?: any) => void
}

let promisedQueue: ConfirmDialogState[] = []

const NAMESPACE = 'ConfirmDialog/'
const showAction = createAction<ConfirmDialogState>(NAMESPACE + 'show')
const setFormModelAction = createAction<CFModelFlat>(NAMESPACE + 'setFormModel')
const resolveAction = createAction<any>(NAMESPACE + 'resolve')
const rejectAction = createAction<void>(NAMESPACE + 'reject')
const ConfirmDialogDefaultState = {
	open: false,
	variant: ConfirmVariant.OK,
	message: '',
	title: '',
	cfDefs: [],
	formModel: {},
	defaultSize: fallbackDefaultSize,
}
const bkAttrs = ['open', 'resolve', 'reject']

function isEqualAlert(p1: Partial<ConfirmDialogState>, p2: Partial<ConfirmDialogState>) {
	return isEqual(omit(p1, ...bkAttrs), omit(p2, ...bkAttrs))
}

function doResolve(s: ConfirmDialogState, payload: any) {
	if (!s.resolve) {
		log.error("missing resolve: already done?", s)
	} else {
		s.resolve(s.cfDefs?.length ? {model: s.formModel, result: payload} : payload)
		return true
	}
	return false // not resolved
}

function doReject(s: ConfirmDialogState) {
	if (!s.reject) {
		log.error("missing reject: already done?", s)
	} else {
		if (s.cfDefs.length) {
			s.reject({model: s.formModel})
		} else {
			s.reject() // no error because it is a user-initiated action (error may be shown of succeeding error handlers)
		}
		return true
	}
	return false // not rejected
}

const reducer = createReducer<ConfirmDialogState>(ConfirmDialogDefaultState, builder => builder
	.addCase(showAction, (state, {payload}) => {
		if (state.resolve || state.reject) {
			if (!state.open) throw new Error("There is was a promise not answered");
			promisedQueue.push(payload)
			return state;
		}
		return {...payload, open: true}
	})
	.addCase(setFormModelAction, (state, {payload}) => ({
		...state,
		formModel: payload,
	}))
	// todo validate form
	.addCase(resolveAction, (state, {payload}) => {
		doResolve(state, payload)
		promisedQueue = promisedQueue.filter((s) => !isEqualAlert(state, s) || !doResolve(s, payload))
		return {
			...state,
			resolve: undefined,
			reject: undefined,
			open: false,
		}
	})
	.addCase(rejectAction, (state) => {
		doReject(state)
		promisedQueue = promisedQueue.filter((s) => !isEqualAlert(state, s) || !doReject(s))
		return {
			...state,
			resolve: undefined,
			reject: undefined,
			open: false,
		}
	}),
)

function prepareAlertParams(params: Partial<ConfirmDialogState>) {
	let {variant, message, title, cfDefs, formModel, defaultSize, resolve, reject} = params
	if (variant == null) variant = ConfirmVariant.OK
	if (title == null) title = ''
	if (message == null) message = ''
	if (!cfDefs) cfDefs = []
	if (!formModel) formModel = {}
	defaultSize = {...fallbackDefaultSize, ...defaultSize}
	return {
		...params,
		open: false,
		variant,
		message,
		title,
		cfDefs,
		defaultSize,
		formModel,
		resolve,
		reject,
	}
}

let initialized = false;

// is changed after mount of ConfirmDialogContainer
// used outside of container in "Confirm"
let _showDialogCB = (params: AlertParams) => {
	const p = new Promise((resolve, reject) => {
		promisedQueue.push(prepareAlertParams({...params, resolve, reject}))
	})
	p.then((result) => log.debug('user said yes', result), (e) => log.debug('user said no', e)) //ignore uncatched error message
	return p
}

export function ConfirmDialogContainer() {
	const [state = ConfirmDialogDefaultState as ConfirmDialogState, dispatch] = useReducer(reducer, ConfirmDialogDefaultState)
	if (!initialized) {
		_showDialogCB = (params: AlertParams) => {
			const p = new Promise(function (resolve, reject) {
				dispatch(showAction(prepareAlertParams({...params, resolve, reject})))
			});
			p.then(() => log.debug('user said yes'), () => log.debug('user said no')) //ignore uncatched error message
			return p;
		}
		initialized = true
	}
	if (promisedQueue.length && !state.open) {
		const x = promisedQueue.shift()
		if (x) dispatch(showAction(x))
	}
	const theme = useTheme()
	const cssWrapper = css`
		height: calc(100% - ${theme.spacing(8)});
		overflow: auto;
		background: inherit;
	`
	const cssTitle = css`
		background: inherit;
		white-space: pre-wrap;
	`
	const cssMessage = css`
		margin: ${theme.spacing(0, 3, 1, 3)};
		white-space: pre-wrap;
	`
	const cssDialogActions = css`
		position: absolute;
		right: 0;
		bottom: 0;
		width: 100%;
		background: inherit;
		text-align: right;
	`
	const cssButton = css`
		margin: ${theme.spacing(1, 2)};
	`
	let resolve = (result?: any) => {
		(window.document.activeElement as HTMLElement | undefined)?.blur()
		setTimeout(() => {
			dispatch(resolveAction(result))
		}, state.cfDefs.length ? 400 : 1)
	}
	let reject = () => {
		(window.document.activeElement as HTMLElement | undefined)?.blur()
		setTimeout(() => {
			dispatch(rejectAction())
		}, state.cfDefs.length ? 400 : 1)
	}
	let buttons
	switch (state.variant) {
		case ConfirmVariant.JN:
			buttons = <>
				<Button variant={'contained'} css={cssButton} color={'secondary'} onClick={() => resolve()} autoFocus>Ja</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject}>Nein</ErrorButton>
			</>
			break
		case ConfirmVariant.JN_CANCEL:
			buttons = <>
				<Button variant={'contained'} css={cssButton} color={'secondary'} onClick={() => resolve(true)} autoFocus>Ja</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={() => resolve(false)}>Nein</ErrorButton>
				<Button variant={'contained'} css={cssButton} onClick={reject} autoFocus>Abbrechen</Button>
			</>
			break;
		case ConfirmVariant.ERROR:
			buttons = <ErrorButton variant={'contained'} css={cssButton} onClick={reject} autoFocus>OK</ErrorButton>
			break
		case ConfirmVariant.REFRESH:
			buttons = <>
				<Button variant={'contained'} css={cssButton} color={'secondary'} onClick={() => resolve()} autoFocus>Jetzt aktualisieren</Button>
				<Button variant={'contained'} css={cssButton} onClick={reject}>Abbrechen</Button>
			</>
			break
		case ConfirmVariant.RETURN:
			buttons = <>
				<Button variant={'contained'} css={cssButton} color={'secondary'} onClick={() => resolve()} autoFocus>Weiter bearbeiten</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject}>Schließen</ErrorButton>
			</>
			break
		case ConfirmVariant.SYNC_DISCARD_ABORT:
			buttons = <>
				<Button variant={'contained'} css={cssButton} color={'secondary'} onClick={() => resolve(true)} autoFocus>Synchronisieren</Button>
				<Button variant={'contained'} css={cssButton} color={'primary'} onClick={() => resolve(false)}>Verwerfen</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject}>Abbrechen</ErrorButton>
			</>
			break;
		case ConfirmVariant.APPLY_RESET_ABORT:
			buttons = <>
				<Button variant={'contained'} css={cssButton} color={'secondary'} onClick={() => resolve(true)} autoFocus>Übernehmen</Button>
				<Button variant={'contained'} css={cssButton} color={'primary'} onClick={() => resolve(false)}>Zurücksetzen</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject}>Abbrechen</ErrorButton>
			</>
			break;
		case ConfirmVariant.DISCARD_KEEP:
			buttons = <>
				<ErrorButton variant={'contained'} css={cssButton} onClick={() => resolve()} autoFocus>Verwerfen</ErrorButton>
				<Button variant={'contained'} css={cssButton} onClick={reject}>Behalten</Button>
			</>
			break
		case ConfirmVariant.OK_CANCEL:
			buttons = <>
				<Button color={'secondary'} variant={'contained'} css={cssButton} onClick={() => resolve()} autoFocus>OK</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject} autoFocus>Abbrechen</ErrorButton>
			</>
			break
		case ConfirmVariant.DELETE_CANCEL:
			buttons = <>
				<ErrorButton variant={'contained'} css={cssButton} onClick={() => resolve()} autoFocus>Löschen</ErrorButton>
				<Button variant={'contained'} css={cssButton} onClick={reject} autoFocus>Abbrechen</Button>
			</>
			break
		case ConfirmVariant.DUPLICATE_CANCEL:
			buttons = <>
				<Button color={'secondary'} variant={'contained'} css={cssButton} onClick={() => resolve()} autoFocus>Duplizieren</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject} autoFocus>Abbrechen</ErrorButton>
			</>
			break
		case ConfirmVariant.RESET_OK:
			buttons = <Button color={'success'} variant={'contained'} css={cssButton} onClick={() => resolve()} autoFocus>Zurück zum Login</Button>
			break
		case ConfirmVariant.RESET_ERROR:
			buttons = <>
				<Button color={'primary'} variant={'contained'} css={cssButton} onClick={() => resolve()} autoFocus>Erneut Eingeben</Button>
				<ErrorButton variant={'contained'} css={cssButton} onClick={reject} autoFocus>Zurück zum Login</ErrorButton>
			</>
			break
		case ConfirmVariant.RESET_VALIDATE_ERROR:
			buttons = <ErrorButton variant={'contained'} css={cssButton} onClick={reject} autoFocus>Zurück zum Login</ErrorButton>
			break
		default:
		case ConfirmVariant.OK:
			buttons = <Button variant={'contained'} css={cssButton} onClick={() => resolve()} color={'primary'} autoFocus>OK</Button>
			break
	}

	const setCFValue: SetCFCb = useCallback((p) => {
		const model = CFimmutable.setCFValue(state.formModel, p.cfDef, p.value, p.options)
		dispatch(setFormModelAction(model))
	}, [state.formModel])

	return <ModalComponent
		open={state.open}
		onClose={reject}
		defaultSize={state.defaultSize || fallbackDefaultSize}
		closeOnBackdropClick={false}
	>
		<div css={cssWrapper}>
			<DialogTitle css={cssTitle}>{state.title}</DialogTitle>
			<Typography component={'div'} css={cssMessage}>
				{state.message}
			</Typography>
			<Typography component={'div'} css={cssMessage}>
				<CustomFieldFormComponent
					customFieldDefinitions={state.cfDefs}
					actionPending={false}
					changePermitted={true}
					model={state.formModel}
					setCFValue={setCFValue}
				/>
			</Typography>
		</div>
		<DialogActions css={cssDialogActions}>
			{buttons}
		</DialogActions>
	</ModalComponent>
}

function defaultArgs(titleOrMessage?: ReactNode, message?: ReactNode, defaultTitle?: ReactNode) {
	let title;
	if (message === undefined) {
		message = titleOrMessage
		title = undefined
	} else {
		title = titleOrMessage
	}
	if (defaultTitle && title == null) title = defaultTitle
	return {title, message}
}

/**
 * all calls require ConfirmDialogContainer mounted in ReactRoot
 */

/** info: OK = resolve */
export function alertInfo(titleOrMessage: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.OK})
}

/** error: OK = reject */
export function alertError(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message, 'Fehler'), variant: ConfirmVariant.ERROR})
}

/** error: OK = reject */
export function _alertServerErrorParsed(message?: ReactNode, title?: ReactNode): Promise<unknown> {
	let args = defaultArgs(title, message, 'Fehler');
	if (!args.message) args = UNKNOWN_ERROR
	return _showDialogCB({...args, variant: ConfirmVariant.ERROR})
}

/** error: OK = reject */
export function alertServerError(error?: any, title?: ReactNode) {
	return _alertServerErrorParsed(parseServerError(error), title)
}

/** Server error handler for RxJS */
export function catchServerError<T extends ObservableInput<any>>(returnValue?: T) {
	return catchError((err: any) => {
		void alertServerError(err)
		return returnValue ?? []
	})
}

/** ask: J = resolve, N = reject */
export function confirmJN(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.JN})
}


export function confirmRequestError(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.RESET_VALIDATE_ERROR})
}

export function confirmPswResetOK(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.RESET_OK})
}

export function confirmPswResetError(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.RESET_ERROR})
}

export function confirmDialog(
	variant: ConfirmVariant,
	titleOrMessage?: ReactNode,
	message?: ReactNode,
	options?: Pick<AlertParams, 'variant' | 'cfDefs' | 'defaultSize'>,
): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), ...options, variant})
}